import { Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import type { StagedAttachment } from '@/lib/staged-attachment'

// Entrance motion shared with the dashboard message list (v2/messages.tsx:44).
// fade + a short slide-up, ease-out, ≤200ms — and disabled under
// prefers-reduced-motion. Never scale(0): the thumbnail has a real shape.
const enterMotion =
  'animate-in fade-in slide-in-from-bottom-2 duration-200 ease-out motion-reduce:animate-none'

/**
 * `<AttachmentChip />` — the staged photo as it sits in the composer: a small
 * thumbnail + a status line + a ✕ to discard. Mirrors the pending-photo
 * pattern in `v2/ThreadChat.tsx` (size-12 rounded-lg border object-cover).
 *
 * Three statuses:
 *   • analyzing → calm "Reading photo…" with a spinner (classify ~1-2s, so no
 *     skeleton — an inline spinner reads as "working", not "broken").
 *   • ready     → the summary line (or a generic "Photo attached").
 *   • error     → a quiet error line; the user can ✕ and re-attach.
 *
 * The action chips live in a sibling `<AttachmentSuggestions />`; this is just
 * the preview + dismiss.
 */
export function AttachmentChip({
  staged,
  onClear,
}: {
  staged: StagedAttachment
  onClear: () => void
}) {
  const { t } = useTranslation('dashboard')

  const statusLine =
    staged.status === 'analyzing'
      ? t('agentic.attach.staged.analyzing')
      : staged.status === 'error'
        ? t('agentic.attach.staged.error')
        : (staged.classification?.summary?.trim() ||
          t('agentic.attach.staged.ready'))

  return (
    <div
      className={`mb-2 flex items-center gap-2.5 ${enterMotion}`}
      aria-live="polite"
    >
      <img
        src={staged.previewUrl}
        alt={t('agentic.attach.thumbnail_alt')}
        className="size-12 shrink-0 rounded-lg border border-border object-cover"
      />
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {staged.status === 'analyzing' ? (
          <Loader2
            className="size-4 shrink-0 animate-spin text-muted-foreground"
            aria-hidden
          />
        ) : null}
        <span
          className={`truncate text-xs ${
            staged.status === 'error'
              ? 'text-destructive'
              : 'text-muted-foreground'
          }`}
        >
          {statusLine}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="pill-icon-sm"
        onClick={onClear}
        aria-label={t('agentic.attach.staged.remove')}
        className="ml-auto min-h-11 min-w-11 shrink-0 text-muted-foreground"
      >
        <X className="size-4" />
      </Button>
    </div>
  )
}
